import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import IntegrationCard from './components/IntegrationCard';
import ApiKeyManager from './components/ApiKeyManager';
import EnvironmentSwitcher from './components/EnvironmentSwitcher';
import ConnectionTester from './components/ConnectionTester';
import WebhookConfiguration from './components/WebhookConfiguration';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const IntegrationsManagement = () => {
  const [currentEnvironment, setCurrentEnvironment] = useState('production');
  const [activeTab, setActiveTab] = useState('integrations');
  const [integrations, setIntegrations] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [environments, setEnvironments] = useState([]);

  // Mock data initialization
  useEffect(() => {
    const mockIntegrations = [
      {
        id: 'github',
        name: 'GitHub',
        category: 'Code Repository',
        description: 'Connect to GitHub repositories for automated code deployment and version control integration with AI agents.',
        icon: 'Github',
        iconColor: 'text-white',
        bgColor: 'bg-gray-900',
        status: 'connected',
        lastSync: new Date(Date.now() - 300000)?.toISOString(),
        dataPoints: 1247,
        errorMessage: null
      },
      {
        id: 'make',
        name: 'Make.com',
        category: 'Workflow Automation',
        description: 'Automate complex workflows and data processing pipelines with visual scenario builder and API integrations.',
        icon: 'Zap',
        iconColor: 'text-white',
        bgColor: 'bg-purple-600',
        status: 'connected',
        lastSync: new Date(Date.now() - 120000)?.toISOString(),
        dataPoints: 3456,
        errorMessage: null
      },
      {
        id: 'slack',
        name: 'Slack',
        category: 'Communication',
        description: 'Send real-time notifications and alerts to your team channels when agents complete tasks or encounter issues.',
        icon: 'MessageSquare',
        iconColor: 'text-white',
        bgColor: 'bg-green-600',
        status: 'error',
        lastSync: new Date(Date.now() - 7200000)?.toISOString(),
        dataPoints: 0,
        errorMessage: 'Authentication token expired. Please reconnect your Slack workspace.'
      },
      {
        id: 'powerbi',
        name: 'Power BI',
        category: 'Business Intelligence',
        description: 'Export processed data and insights directly to Power BI dashboards for advanced visualization and reporting.',
        icon: 'BarChart3',
        iconColor: 'text-white',
        bgColor: 'bg-yellow-600',
        status: 'pending',
        lastSync: null,
        dataPoints: 0,
        errorMessage: null
      },
      {
        id: 'airtable',
        name: 'Airtable',
        category: 'Database',
        description: 'Sync structured data and research findings to Airtable bases for collaborative data management and analysis.',
        icon: 'Database',
        iconColor: 'text-white',
        bgColor: 'bg-blue-600',
        status: 'disconnected',
        lastSync: new Date(Date.now() - 86400000)?.toISOString(),
        dataPoints: 892,
        errorMessage: null
      },
      {
        id: 'codex',
        name: 'Codex Cloud',
        category: 'AI Platform',
        description: 'Connect to Codex Cloud for advanced AI model deployment and distributed computing resources.',
        icon: 'Cloud',
        iconColor: 'text-white',
        bgColor: 'bg-indigo-600',
        status: 'connected',
        lastSync: new Date(Date.now() - 60000)?.toISOString(),
        dataPoints: 5678,
        errorMessage: null
      }
    ];

    const mockApiKeys = [
      {
        id: 'key1',
        name: 'Production Dashboard',
        value: 'ipsos_live_4f8a9b2c1d3e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6',
        status: 'active',
        createdAt: new Date(Date.now() - 2592000000)?.toISOString(),
        lastUsed: new Date(Date.now() - 3600000)?.toISOString(),
        requestCount: 15420,
        expiresAt: new Date(Date.now() + 31536000000)?.toISOString()
      },
      {
        id: 'key2',
        name: 'Mobile Application',
        value: 'ipsos_mobile_7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f3g4h5',
        status: 'active',
        createdAt: new Date(Date.now() - 1296000000)?.toISOString(),
        lastUsed: new Date(Date.now() - 7200000)?.toISOString(),
        requestCount: 8934,
        expiresAt: null
      },
      {
        id: 'key3',
        name: 'Legacy Integration',
        value: 'ipsos_legacy_1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c',
        status: 'expired',
        createdAt: new Date(Date.now() - 7776000000)?.toISOString(),
        lastUsed: new Date(Date.now() - 2592000000)?.toISOString(),
        requestCount: 45678,
        expiresAt: new Date(Date.now() - 86400000)?.toISOString()
      }
    ];

    const mockWebhooks = [
      {
        id: 'webhook1',
        name: 'Slack Notifications',
        url: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX',
        events: ['agent.launched', 'data.processed', 'integration.failed'],
        secret: 'whsec_1234567890abcdef',
        active: true,
        status: 'active',
        createdAt: new Date(Date.now() - 1296000000)?.toISOString(),
        lastTriggered: new Date(Date.now() - 1800000)?.toISOString(),
        deliveryCount: 342,
        successRate: '98.5%'
      },
      {
        id: 'webhook2',
        name: 'Report Generation Alert',
        url: 'https://api.company.com/webhooks/reports',
        events: ['report.generated', 'sync.completed'],
        secret: 'whsec_abcdef1234567890',
        active: true,
        status: 'active',
        createdAt: new Date(Date.now() - 604800000)?.toISOString(),
        lastTriggered: new Date(Date.now() - 3600000)?.toISOString(),
        deliveryCount: 89,
        successRate: '100%'
      }
    ];

    const mockEnvironments = [
      {
        key: 'production',
        name: 'Production',
        description: 'Live environment with real data and active integrations',
        endpoint: 'api.ipsos-insight.com',
        status: 'healthy',
        activeConnections: 6,
        lastSync: '2 minutes ago',
        apiCalls: 45678,
        uptime: '99.9%'
      },
      {
        key: 'sandbox',
        name: 'Sandbox',
        description: 'Safe testing environment with sample data',
        endpoint: 'sandbox-api.ipsos-insight.com',
        status: 'healthy',
        activeConnections: 3,
        lastSync: '15 minutes ago',
        apiCalls: 12345,
        uptime: '99.5%'
      },
      {
        key: 'development',
        name: 'Development',
        description: 'Development environment for testing new features',
        endpoint: 'dev-api.ipsos-insight.com',
        status: 'warning',
        activeConnections: 2,
        lastSync: '1 hour ago',
        apiCalls: 5432,
        uptime: '95.2%'
      }
    ];

    setIntegrations(mockIntegrations);
    setApiKeys(mockApiKeys);
    setWebhooks(mockWebhooks);
    setEnvironments(mockEnvironments);
  }, []);

  const handleConnect = (integration) => {
    setIntegrations(prev => prev?.map(int => 
      int?.id === integration?.id 
        ? { ...int, status: 'pending' }
        : int
    ));

    // Simulate connection process
    setTimeout(() => {
      setIntegrations(prev => prev?.map(int => 
        int?.id === integration?.id 
          ? { 
              ...int, 
              status: 'connected', 
              lastSync: new Date()?.toISOString(),
              dataPoints: Math.floor(Math.random() * 5000) + 1000
            }
          : int
      ));
    }, 2000);
  };

  const handleDisconnect = (integration) => {
    setIntegrations(prev => prev?.map(int => 
      int?.id === integration?.id 
        ? { ...int, status: 'disconnected', dataPoints: 0 }
        : int
    ));
  };

  const handleConfigure = (integration) => {
    console.log('Configure integration:', integration);
    // Open configuration modal or navigate to settings
  };

  const handleGenerateApiKey = (name) => {
    const newKey = {
      id: `key${Date.now()}`,
      name,
      value: `ipsos_${Date.now()}_${Math.random()?.toString(36)?.substring(2, 15)}`,
      status: 'active',
      createdAt: new Date()?.toISOString(),
      lastUsed: null,
      requestCount: 0,
      expiresAt: null
    };
    
    setApiKeys(prev => [newKey, ...prev]);
  };

  const handleRevokeApiKey = (keyId) => {
    setApiKeys(prev => prev?.filter(key => key?.id !== keyId));
  };

  const handleCreateWebhook = (webhook) => {
    setWebhooks(prev => [webhook, ...prev]);
  };

  const handleUpdateWebhook = (webhookId, updates) => {
    setWebhooks(prev => prev?.map(webhook => 
      webhook?.id === webhookId ? { ...webhook, ...updates } : webhook
    ));
  };

  const handleDeleteWebhook = (webhookId) => {
    setWebhooks(prev => prev?.filter(webhook => webhook?.id !== webhookId));
  };

  const tabs = [
    { id: 'integrations', label: 'Integrations', icon: 'Plug' },
    { id: 'api-keys', label: 'API Keys', icon: 'Key' },
    { id: 'environment', label: 'Environment', icon: 'Globe' },
    { id: 'testing', label: 'Connection Testing', icon: 'Zap' },
    { id: 'webhooks', label: 'Webhooks', icon: 'Webhook' }
  ];

  const connectedCount = integrations?.filter(int => int?.status === 'connected')?.length;
  const totalIntegrations = integrations?.length;

  return (
    <>
      <Helmet>
        <title>Integrations Management - Ipsos Insight Intelligence Platform</title>
        <meta name="description" content="Configure and monitor third-party service connections for automated workflows. Manage GitHub, Make.com, Slack, Power BI, and Airtable integrations with real-time status monitoring." />
        <meta property="og:title" content="Integrations Management - Ipsos Insight Intelligence Platform" />
        <meta property="og:description" content="Enterprise integration management for AI-driven market research automation" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Integrations Management
                </h1>
                <p className="text-muted-foreground mb-4">
                  Configure and monitor third-party service connections for automated workflows
                </p>
              </div>
              
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-card border border-border rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft" />
                    <span className="text-sm font-medium text-card-foreground">
                      {connectedCount}/{totalIntegrations} Connected
                    </span>
                  </div>
                </div>
                
                <Button
                  variant="default"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => window.location?.reload()}
                >
                  Refresh Status
                </Button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="mb-8">
              <div className="border-b border-border">
                <nav className="flex space-x-8">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
              {activeTab === 'integrations' && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {integrations?.map((integration) => (
                      <IntegrationCard
                        key={integration?.id}
                        integration={integration}
                        onConnect={handleConnect}
                        onDisconnect={handleDisconnect}
                        onConfigure={handleConfigure}
                      />
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'api-keys' && (
                <ApiKeyManager
                  apiKeys={apiKeys}
                  onGenerateKey={handleGenerateApiKey}
                  onRevokeKey={handleRevokeApiKey}
                  onUpdateKey={() => {}}
                />
              )}

              {activeTab === 'environment' && (
                <EnvironmentSwitcher
                  currentEnvironment={currentEnvironment}
                  onEnvironmentChange={setCurrentEnvironment}
                  environments={environments}
                />
              )}

              {activeTab === 'testing' && (
                <ConnectionTester
                  integrations={integrations}
                  onTestConnection={(integrationId, results) => {
                    console.log('Test results:', integrationId, results);
                  }}
                />
              )}

              {activeTab === 'webhooks' && (
                <WebhookConfiguration
                  webhooks={webhooks}
                  onCreateWebhook={handleCreateWebhook}
                  onUpdateWebhook={handleUpdateWebhook}
                  onDeleteWebhook={handleDeleteWebhook}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default IntegrationsManagement;